import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, Building2, AlertCircle, PlayCircle, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '../context/BrandingContext';

const LoginPage = () => {
  const [, setRerender] = useState(0);
  const [currentLang, setCurrentLang] = useState('es');
  
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const { getCurrentBranding, getThemeClasses, getDomainInfo } = useBranding();
  
  const currentBranding = getCurrentBranding();
  const themeClasses = getThemeClasses();
  const domainInfo = getDomainInfo();
  
  // Detectar idioma actual de Google Translate
  React.useEffect(() => {
    const detectLanguage = () => {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'googtrans') {
          const lang = value.split('/')[2];
          if (lang && ['es', 'en', 'pt'].includes(lang)) {
            setCurrentLang(lang);
            return;
          }
        }
      }
      setCurrentLang('es');
    };
    
    detectLanguage();
    const interval = setInterval(detectLanguage, 500);
    return () => clearInterval(interval);
  }, []);
  
  // Debug: mostrar branding actual en consola - solo en desarrollo
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔑 LoginPage - Current branding:', currentBranding);
      console.log('🎨 LoginPage - Theme classes:', themeClasses);
      console.log('🌐 LoginPage - Domain info:', domainInfo);
      console.log('🏭 LoginPage - Production mode would show:', {
        companyName: currentBranding.name,
        description: currentBranding.description,
        colors: themeClasses.primary,
        hasDebugElements: false
      });
    }
  }, [currentBranding, themeClasses, domainInfo]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [backendResult, setBackendResult] = useState(null);

  React.useEffect(() => {
    const handler = () => setRerender(v => v + 1);
    i18n.on('languageChanged', handler);
    return () => i18n.off('languageChanged', handler);
  }, [i18n]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setBackendResult(null);
    
    console.log('Attempting login with identifier:', { identifier: email, password });
    const result = await login(email, password);
    console.log('Login result:', result);
    setBackendResult(result);
    
    if (!result.success) {
      // Mensaje específico si no tiene acceso al tenant actual
      if (result.error === 'Sin acceso a esta empresa' && result.availableCompanies) {
        const companies = result.availableCompanies.map(c => c.name).join(', ');
        const errorMessage = `🚫 No tienes acceso a ${currentBranding.name}.\n\n✅ Tus empresas disponibles: ${companies}`;
        setError(errorMessage);
        
        // También mostrar alerta del navegador para que sea más visible
        setTimeout(() => {
          alert(`⚠️ ACCESO DENEGADO\n\nNo tienes permisos para acceder a ${currentBranding.name}.\n\nTus empresas disponibles son:\n• ${companies}\n\nPor favor, accede desde el dominio correcto o contacta al administrador.`);
        }, 100);
      } else {
        setError(result.error);
      }
      setLoading(false);
      return;
    }
    
    console.log('Login successful, navigating...');
    navigate('/');
    setLoading(false);
  };

  return (
    <div className={`min-h-screen ${themeClasses.headerBg} flex flex-col`}>
      <div className="flex items-center justify-center p-4 flex-1">
        <div className="max-w-md w-full bg-white/90 shadow-xl rounded-3xl px-8 py-10 border border-gray-200 relative">
          <div className="mb-6 flex justify-between items-center">
            {process.env.NODE_ENV === 'development' && (
              <div className="text-xs text-gray-500">
                <div>Tenant: {currentBranding.name}</div>
                <div>Domain: {domainInfo.hostname}</div>
                <div>Mode: {domainInfo.isProduction ? 'Prod' : 'Dev'}</div>
              </div>
            )}
            <div className={process.env.NODE_ENV === 'development' ? '' : 'ml-auto'}>
              <select
                value={currentLang}
                onChange={(e) => {
                  const lang = e.target.value;
                  if (window.changeLanguage) {
                    window.changeLanguage(lang);
                  }
                }}
                className="text-sm bg-transparent border border-gray-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                <option value="es">Español</option>
                <option value="en">English</option>
                <option value="pt">Português</option>
              </select>
            </div>
          </div>

          <div className="text-center mb-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="flex items-center justify-center mb-4"
            >
              {currentBranding.logo ? (
                <img 
                  src={currentBranding.logo} 
                  alt={currentBranding.name} 
                  className="w-12 h-12 rounded-lg mr-3 object-cover"
                />
              ) : (
                <Building2 className={`w-12 h-12 ${themeClasses.accent} mr-3`} />
              )}
              <h1 className="text-3xl font-bold text-gray-800">{currentBranding.name}</h1>
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-gray-600"
            >
              {currentBranding.description}
            </motion.p>
          </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email o Usuario
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="correo@ejemplo.com o usuario"
                required
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('password')}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder={t('passwordPlaceholder')}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </motion.div>

          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col gap-2 p-4 bg-red-50 border-2 border-red-300 rounded-xl text-red-700 shadow-lg"
            >
              <div className="flex items-center gap-2">
                <AlertCircle className="w-6 h-6 flex-shrink-0" />
                <span className="text-sm font-semibold">Error de acceso</span>
              </div>
              <span className="text-sm whitespace-pre-line leading-relaxed">{error}</span>
            </motion.div>
          )}
          {backendResult && (
            <div className="mt-2 p-2 bg-gray-100 border border-gray-300 rounded text-xs text-gray-700">
              <strong>Respuesta backend:</strong>
              <pre>{JSON.stringify(backendResult, null, 2)}</pre>
            </div>
          )}

          <motion.button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-4 rounded-xl font-semibold text-white transition-all ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
            whileHover={{ scale: loading ? 1 : 1.02 }}
            whileTap={{ scale: loading ? 1 : 0.98 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            {loading ? t('loading') : t('login')}
          </motion.button>

          {/* Enlaces de ayuda */}
          <motion.div
            className="mt-6 space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            {currentBranding.email && (
              <p className="text-sm text-gray-600 text-center">
                Solicita tu clave de acceso a:{' '}
                <a 
                  href={`mailto:${currentBranding.email}`}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  {currentBranding.email}
                </a>
              </p>
            )}
            
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              {currentBranding.tutorialVideoUrl && (
                <a
                  href={currentBranding.tutorialVideoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                >
                  <PlayCircle className="w-4 h-4" />
                  Ver video tutorial del Tarifario
                </a>
              )}
              
              {currentBranding.website && (
                <a
                  href={currentBranding.website.startsWith('http') ? currentBranding.website : `https://${currentBranding.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Volver a {currentBranding.website.replace('https://', '').replace('http://', '')}
                </a>
              )}
            </div>
          </motion.div>
        </form>
        
        {/* Botones de test para cambiar tenant - solo en desarrollo */}
        {process.env.NODE_ENV === 'development' && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-4 pt-4 border-t border-gray-200"
          >
            <div className="text-xs text-gray-500 mb-2">Test: Cambiar empresa</div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  localStorage.setItem('current_tenant', 'chirakChile');
                  window.location.reload();
                }}
                className="flex-1 text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors"
              >
                Chile
              </button>
              <button
                onClick={() => {
                  localStorage.setItem('current_tenant', 'chirakPeru');
                  window.location.reload();
                }}
                className="flex-1 text-xs px-2 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200 transition-colors"
              >
                Perú
              </button>
            </div>
          </motion.div>
        )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;